<script>
import { useAlert } from 'dashboard/composables';
import { mapActions } from 'vuex';

export default {
  name: "CreateGSheetModal",
  props: {
    isEditMode: {
      type: Boolean,
      default: false
    },
    editObj: {
      type: Object
    }
  },
  data() {
    return {
      payload: {
        name: '',
        gsheet_url: '',
        description: '',
        auto_sync: true
      },
      errMsg: {
        name: '',
        gsheet_url: '',
      },
      isLoading: false,
    }
  },
  computed: {
    isFormValid() {
      if (!this.isEditMode) {
        return this.payload.name.length > 0 && this.payload.gsheet_url.length > 0;
      }
      return this.payload.description.length >= 0; // description can be empty
    }
  },
  methods: {
    ...mapActions('gsheet', ['createKnowledge', 'editKnowledge', 'editKnowledgeDescription']),
    async handleConfirm() {
      this.errMsg = {
        name: '',
        gsheet_url: '',
      }

      this.isLoading = true;

      if (!this.isFormValid) {
        useAlert('Make sure all required fields are filled');
        if (!this.isEditMode) {
          if (this.payload.name.length <= 0) {
            this.errMsg.name = 'This field is required';
          }
          if (this.payload.gsheet_url.length <= 0) {
            this.errMsg.gsheet_url = 'This field is required';
          }
        }
        return;
      }

      try {
        if (!this.isEditMode) {
          await this.createKnowledge(this.payload);
        } else {
          await this.editKnowledgeDescription({ 
            knowledgeId: this.editObj.id, 
            payload: { description: this.payload.description }
          });
        }
        useAlert(`Knowledge ${this.isEditMode ? 'description updated' : 'created'} successfully`);
        this.$emit('close');
        this.$emit('refresh');
      } catch (err) {
        if (Array.isArray(err)) {
          useAlert(err.join('\n'));
        } else {
          useAlert(`Failed to ${this.isEditMode ? 'update description' : 'creat'} knowledge`);
          console.error(err);
        }
      }finally{
        this.isLoading = false;
      }
    }
  },
  mounted() {
    if (this.isEditMode) {
      this.payload.name = this.editObj.name;
      this.payload.gsheet_url = this.editObj.gsheet_url;
      this.payload.description = this.editObj.description || '';
    }
  }
}
</script>

<template>
  <div class="fixed inset-0 flex items-center justify-center bg-slate-800/80 dark:bg-slate-800/80 z-50">
    <div class="bg-white dark:bg-slate-900 rounded-lg p-6 shadow-lg w-96 max-h-[80vh] overflow-y-auto border border-slate-200 dark:border-slate-700">
      <!-- header -->
      <div class="flex items-center mb-4 justify-between">
        <h1 class="text-xl font-bold text-slate-900 dark:text-slate-100">
          {{ isEditMode ? 'Edit Knowledge Description' : 'Create Knowledge' }}
        </h1>
        <woot-button
          variant="clear"
          color-scheme="alert"
          icon="dismiss"
          @click="$emit('close')"
        />
      </div>

      <form class="space-y-4">
        <!-- Name -->
        <div>
          <label for="name" class="block mb-1 text-slate-700 dark:text-slate-300">Knowledge Name:</label>
          <input
            v-model="payload.name"
            id="name"
            type="text"
            :disabled="isEditMode"
            placeholder="Enter a name..."
            :class="{
              'reset-base p-2 rounded w-full border bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-woot-500 dark:focus:ring-woot-400': true,
              'opacity-60 cursor-not-allowed': isEditMode,
              'border-slate-200 dark:border-slate-700': !errMsg.name,
              'border-red-500 focus:border-red-500 focus:ring-red-500': errMsg.name
            }"
          />
          <p v-if="errMsg.name && !isEditMode" class="mt-1 text-sm text-red-500 dark:text-red-400">
            {{ errMsg.name }}
          </p>
        </div>

        <!-- Address -->
        <div>
          <label for="gsheet_url" class="block mb-1 text-slate-700 dark:text-slate-300">Address:</label>
          <input
            v-model="payload.gsheet_url"
            id="gsheet_url"
            type="text"
            :disabled="isEditMode"
            placeholder="Enter a gsheet_url..."
            :class="{
              'reset-base p-2 rounded w-full border bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-woot-500 dark:focus:ring-woot-400': true,
              'opacity-60 cursor-not-allowed': isEditMode,
              'border-slate-200 dark:border-slate-700': !errMsg.gsheet_url,
              'border-red-500 focus:border-red-500 focus:ring-red-500': errMsg.gsheet_url
            }"
          />
          <p v-if="errMsg.gsheet_url && !isEditMode" class="mt-1 text-sm text-red-500 dark:text-red-400">
            {{ errMsg.gsheet_url }}
          </p>
        </div>

        <!-- Description -->
        <div>
          <label for="description" class="block mb-1 text-slate-700 dark:text-slate-300">Description:</label>
          <textarea
            v-model="payload.description"
            id="description"
            rows="3"
            placeholder="Enter a description..."
            class="reset-base p-2 rounded w-full border bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-woot-500 dark:focus:ring-woot-400 border-slate-200 dark:border-slate-700"
          />
        </div>
      </form>

      <!-- Action buttons -->
      <div class="flex justify-end gap-4 mt-6">
        <woot-button color-scheme="secondary" @click="$emit('close')">
          Cancel
        </woot-button>
        <woot-button @click="handleConfirm" :is-loading="isLoading">
          {{ isEditMode ? 'Update' : 'Confirm' }}
        </woot-button>
      </div>
    </div>
  </div>
</template>
